/**
 * ScanStatus Utility Functions
 *
 * Helper functions for ScanStatus components, including status
 * configuration mapping and common constants.
 */

import {
  ArrowPathIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  MinusCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import React from 'react';

import { StatusChipProps } from '@/components/ui/status-chip';
import { JobStatus } from '@/types';
import {
  getVariantForStatus,
  jobStatusVariants,
} from '@/utils/statusChip.util';

/**
 * Maps a JobStatus to the appropriate StatusChip configuration.
 *
 * @param status - The job status to map
 * @returns StatusChipProps configuration or null if status is undefined
 *
 * @example
 * ```tsx
 * const config = getScanStatusConfig(JobStatus.Pass);
 * // { label: 'Completed', variant: 'info', icon: <CheckCircleIcon ... /> }
 *
 * const config = getScanStatusConfig(undefined);
 * // null
 * ```
 */
export const getScanStatusConfig = (
  status?: JobStatus
): StatusChipProps | null => {
  switch (status) {
    case JobStatus.Pass:
      return {
        label: 'Completed',
        variant: getVariantForStatus('completed', jobStatusVariants),
        icon: React.createElement(CheckCircleIcon, {
          className: 'size-3 shrink-0',
        }),
      };
    case JobStatus.Fail:
      return {
        label: 'Failed',
        variant: getVariantForStatus('failed', jobStatusVariants),
        icon: React.createElement(XCircleIcon, {
          className: 'size-3 shrink-0',
        }),
      };
    case JobStatus.Running:
    case JobStatus.AsyncRunning:
    case JobStatus.AsyncReturning:
      return {
        label: 'Running',
        variant: getVariantForStatus('running', jobStatusVariants),
        icon: React.createElement(ArrowPathIcon, {
          className: 'size-3 shrink-0 motion-safe:animate-spin',
        }),
      };
    case JobStatus.Queued:
      return {
        label: 'Queued',
        variant: getVariantForStatus('queued', jobStatusVariants),
        icon: React.createElement(ClockIcon, {
          className: 'size-3 shrink-0',
        }),
      };
    case JobStatus.Partial:
      return {
        label: 'Partial',
        variant: getVariantForStatus('partial', jobStatusVariants),
        icon: React.createElement(ExclamationTriangleIcon, {
          className: 'size-3 shrink-0',
        }),
      };
    default:
      return null;
  }
};

/**
 * Default status configuration for when no scans exist.
 */
export const noScansConfig: StatusChipProps = {
  label: 'No scans',
  variant: getVariantForStatus('no scans', jobStatusVariants),
  icon: React.createElement(MinusCircleIcon, {
    className: 'size-3 shrink-0',
  }),
};
